import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useState, useEffect, useRef } from "react";
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Circle,
  Download,
  Medal,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import apiClient, { apiUrl } from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";
import { PageSkeleton } from "@/components/shared/Skeletons";

export const Route = createFileRoute("/student/courses/$id/learn")({
  component: CourseLearningPage,
});

type Lesson = {
  id: number;
  course_id: number;
  title: string;
  description: string;
  order: number;
  video_url: string;
  pdf_url: string;
  duration: string;
  resources: string; // JSON
  quizzes: string;   // JSON
  completed: boolean;
  video_watched_percent: number;
  pdf_accessed: boolean;
  quiz_completed: boolean;
  quiz_score: number;
};

type QuizQuestion = {
  question: string;
  options: string[];
  correct_index: number;
};

type CourseMeta = {
  id: number;
  title: string;
  category: string;
};

type DriveFile = {
  id: string;
  name: string;
  mimeType: string;
  thumbnailLink?: string;
  webViewLink?: string;
  webContentLink?: string;
  // Short-lived signed path returned by the course resources endpoint. The
  // stream route rejects unsigned requests, so this is what media elements use.
  streamUrl?: string;
};

// Resolve the signed stream path, falling back to the unsigned path only so a
// stale/cached resource payload still renders (the server will refuse it).
const driveStreamSrc = (file: DriveFile): string =>
  apiUrl(file.streamUrl || `/stream/drive/${file.id}`);

function CourseLearningPage() {
  const { id } = Route.useParams();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  
  // Quiz states
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);

  // PDF Modal
  const [pdfOpen, setPdfOpen] = useState(false);
  
  // Tracked states to prevent excessive API hits
  const lastProgressSentRef = useRef<Record<number, number>>({});

  // Fetch Course Meta
  const { data: courseMeta, isLoading: metaLoading } = useQuery({
    queryKey: ["course-meta", id],
    queryFn: async () => {
      if (user?.isDemo) {
        return { id: Number(id), title: "Microsoft Word & Google Docs: Master Document Creation", category: "Business & Productivity" };
      }
      const res = await apiClient.get(`/courses/${id}`);
      return unwrapApiObject<CourseMeta>(res);
    },
  });

  // Fetch Lessons and Student Progress
  const { data: lessons = [], isLoading: lessonsLoading } = useQuery({
    queryKey: ["course-lessons", id],
    queryFn: async () => {
      if (user?.isDemo) {
        return getMockLessons(Number(id));
      }
      const res = await apiClient.get(`/courses/${id}/lessons`);
      return unwrapApiArray<Lesson>(res);
    },
  });

  // Fetch Real-Time Drive Resources
  const { data: driveResources = {}, isLoading: driveLoading } = useQuery({
    queryKey: ["course-drive-resources", id],
    queryFn: async () => {
      if (user?.isDemo) return {};
      const res = await apiClient.get(`/courses/${id}/drive-resources`);
      return res.data.data as Record<string, DriveFile[]>;
    },
  });

  // Update Progress Mutation
  const updateProgressMutation = useMutation({
    mutationFn: async (payload: {
      lesson_id: number;
      lesson_index: number;
      completed?: boolean;
      video_watched_percent?: number;
      pdf_accessed?: boolean;
      quiz_completed?: boolean;
      quiz_score?: number;
    }) => {
      if (user?.isDemo) return { success: true };
      const res = await apiClient.post(`/courses/${id}/progress`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-lessons", id] });
      queryClient.invalidateQueries({ queryKey: ["student-courses"] });
      queryClient.invalidateQueries({ queryKey: ["student-certificates"] });
    },
  });

  const activeLesson = lessons[currentLessonIndex] as Lesson | undefined;

  // Handle Video Progress Tracking inline
  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>, fileId: string) => {
    const video = e.currentTarget;
    if (!video.duration || !activeLesson || user?.isDemo) return;
    const percent = Math.floor((video.currentTime / video.duration) * 100);
    
    // Send updates every 20% milestone to throttle requests
    const lastSentKey = `${activeLesson.id}-${fileId}`;
    const lastSent = lastProgressSentRef.current[lastSentKey] || 0;
    if (percent >= lastSent + 20 || (percent >= 90 && lastSent < 90)) {
      lastProgressSentRef.current[lastSentKey] = percent;
      updateProgressMutation.mutate({
        lesson_id: activeLesson.id,
        lesson_index: currentLessonIndex,
        video_watched_percent: percent,
        completed: percent >= 90,
      });
    }
  };

  const handleEnded = (e: React.SyntheticEvent<HTMLVideoElement>, fileId: string) => {
    if (!activeLesson || user?.isDemo) return;
    const lastSentKey = `${activeLesson.id}-${fileId}`;
    lastProgressSentRef.current[lastSentKey] = 100;
    updateProgressMutation.mutate({
      lesson_id: activeLesson.id,
      lesson_index: currentLessonIndex,
      video_watched_percent: 100,
      completed: true,
    });
  };

  // Reset quiz states when lesson changes
  useEffect(() => {
    setSelectedAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
  }, [currentLessonIndex]);

  if (metaLoading || lessonsLoading) {
    return <PageSkeleton />;
  }

  if (!courseMeta || lessons.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-12 text-center">
        <h1 className="text-title text-destructive">Course learning contents not loaded</h1>
        <Button asChild variant="outline">
          <Link to="/student/courses">Back to Courses</Link>
        </Button>
      </div>
    );
  }

  // Calculate overall course progress percentage
  const completedCount = lessons.filter(l => l.completed).length;
  const overallProgress = Math.round((completedCount / lessons.length) * 100);
  const isCourseFinished = overallProgress >= 100;

  // Parse JSON data for active lesson
  let activeResources: Array<{ title: string; url: string; type: string }> = [];
  let activeQuizzes: QuizQuestion[] = [];

  if (activeLesson) {
    try {
      activeResources = JSON.parse(activeLesson.resources || "[]");
    } catch (e) {
      console.error("Failed to parse resources:", e);
    }
    try {
      activeQuizzes = JSON.parse(activeLesson.quizzes || "[]");
    } catch (e) {
      console.error("Failed to parse quizzes:", e);
    }
  }

  // Extract Drive resources for active lesson
  const activeDriveFiles = activeLesson ? (driveResources[activeLesson.id.toString()] || []) : [];

  // Handle Resource Access Tracking
  const handleResourceView = (file: DriveFile) => {
    window.open(driveStreamSrc(file), "_blank");
    if (activeLesson && !user?.isDemo && !activeLesson.pdf_accessed) {
      const isVideoPresent = activeDriveFiles.some(f => f.mimeType.startsWith("video/"));
      const isQuizPresent = activeQuizzes.length > 0;
      const shouldComplete = !isVideoPresent && !isQuizPresent;
      
      updateProgressMutation.mutate({
        lesson_id: activeLesson.id,
        lesson_index: currentLessonIndex,
        pdf_accessed: true,
        completed: shouldComplete,
      });
    }
  };

  // Handle Quiz Submission
  const handleQuizSubmit = () => {
    if (activeQuizzes.length === 0 || !activeLesson) return;

    let correct = 0;
    activeQuizzes.forEach((q, idx) => {
      if (selectedAnswers[idx] === q.correct_index) {
        correct++;
      }
    });

    setQuizScore(correct);
    setQuizSubmitted(true);

    const isQuizPass = correct >= 2; // Pass criteria: at least 2 correct

    if (!user?.isDemo) {
      updateProgressMutation.mutate({
        lesson_id: activeLesson.id,
        lesson_index: currentLessonIndex,
        quiz_completed: isQuizPass,
        quiz_score: Math.round((correct / activeQuizzes.length) * 100),
        completed: isQuizPass && activeLesson.video_watched_percent >= 90,
      });
    } else {
      // Demo mode completion
      activeLesson.quiz_completed = isQuizPass;
      activeLesson.quiz_score = Math.round((correct / activeQuizzes.length) * 100);
      if (isQuizPass) {
        activeLesson.completed = true;
      }
    }
  };

  return (
    <div>
      <PageHeader
        title={courseMeta.title}
        subtitle={courseMeta.category}
        breadcrumbs={[
          { label: "Dashboard", path: "/student/dashboard" },
          { label: "Courses", path: "/student/courses" },
          { label: "Learning Panel" },
        ]}
        action={
          <Button asChild variant="outline">
            <Link to={`/student/courses/${id}` as never}>
              <ArrowLeft />
              Course details
            </Link>
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Left column - Media & Interactive Area */}
        <div className="min-w-0 lg:col-span-8">
          {activeLesson ? (
            <div className="space-y-5">
              <div>
                <h2 className="mb-1.5 text-[22px] leading-7 font-normal tracking-[-0.4px] text-foreground">
                  {activeLesson.title}
                </h2>
                <p className="text-[15px] leading-relaxed text-body">{activeLesson.description}</p>
              </div>

              {activeDriveFiles.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No learning resources found for this module.
                </p>
              ) : (
                activeDriveFiles.map((f) => {
                  if (f.mimeType.startsWith("video/")) {
                    return (
                      <figure key={f.id} className="overflow-hidden rounded-md border border-border bg-card">
                        <figcaption className="border-b border-border px-4 py-2.5 text-sm font-medium text-foreground">
                          {f.name}
                        </figcaption>
                        <video
                          src={driveStreamSrc(f)}
                          controls
                          controlsList="nodownload"
                          onTimeUpdate={(e) => handleTimeUpdate(e, f.id)}
                          onEnded={(e) => handleEnded(e, f.id)}
                          className="block h-auto max-h-[500px] w-full bg-background"
                        />
                      </figure>
                    );
                  } else if (f.mimeType === "application/pdf") {
                    return (
                      <div key={f.id} className="flex h-[600px] flex-col overflow-hidden rounded-md border border-border bg-card">
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                          <span className="min-w-0 truncate text-sm font-medium text-foreground">{f.name}</span>
                          <Button size="sm" variant="outline" onClick={() => handleResourceView(f)}>
                            <Download />
                            Open fullscreen
                          </Button>
                        </div>
                        <iframe
                          title={f.name}
                          src={driveStreamSrc(f)}
                          width="100%"
                          height="100%"
                          className="grow border-0"
                          onLoad={() => {
                            if (activeLesson && !user?.isDemo && !activeLesson.pdf_accessed) {
                              const isVideoPresent = activeDriveFiles.some(f => f.mimeType.startsWith("video/"));
                              const isQuizPresent = activeQuizzes.length > 0;
                              updateProgressMutation.mutate({
                                lesson_id: activeLesson.id,
                                lesson_index: currentLessonIndex,
                                pdf_accessed: true,
                                completed: !isVideoPresent && !isQuizPresent,
                              });
                            }
                          }}
                        />
                      </div>
                    );
                  } else {
                    return (
                      <div
                        key={f.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body"
                            aria-hidden
                          >
                            <BookOpen className="size-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">{f.name}</p>
                            <p className="text-caption text-muted-foreground">Downloadable Material</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => handleResourceView(f)}>
                          <Download />
                          Download file
                        </Button>
                      </div>
                    );
                  }
                })
              )}

              {activeQuizzes.length > 0 && (
                <SectionCard title="Lesson verification quiz" className="mt-2">
                  <div className="space-y-4">
                    {activeQuizzes.map((q, idx) => (
                      <fieldset key={idx} className="rounded-sm border border-border p-4">
                        <legend className="sr-only">Question {idx + 1}</legend>
                        <p className="mb-3 text-sm font-medium text-foreground" id={`quiz-q-${idx}`}>
                          {idx + 1}. {q.question}
                        </p>
                        <RadioGroup
                          aria-labelledby={`quiz-q-${idx}`}
                          value={selectedAnswers[idx] !== undefined ? String(selectedAnswers[idx]) : ""}
                          onValueChange={(value) => {
                            if (quizSubmitted) return;
                            setSelectedAnswers((prev) => ({
                              ...prev,
                              [idx]: parseInt(value),
                            }));
                          }}
                          className="gap-2"
                        >
                          {q.options.map((opt, optIdx) => {
                            const optionId = `quiz-${activeLesson.id}-${idx}-${optIdx}`;
                            return (
                              <div key={optIdx} className="flex items-center gap-2.5">
                                <RadioGroupItem value={String(optIdx)} id={optionId} />
                                <Label
                                  htmlFor={optionId}
                                  className={cn(
                                    "text-[13px] font-normal",
                                    quizSubmitted && optIdx === q.correct_index
                                      ? "text-success"
                                      : quizSubmitted &&
                                          selectedAnswers[idx] === optIdx &&
                                          optIdx !== q.correct_index
                                        ? "text-destructive"
                                        : "text-body",
                                  )}
                                >
                                  {opt}
                                </Label>
                              </div>
                            );
                          })}
                        </RadioGroup>
                      </fieldset>
                    ))}

                    {quizSubmitted ? (
                      <Alert variant={quizScore >= 2 ? "success" : "destructive"}>
                        {quizScore >= 2 ? <CheckCircle2 /> : <AlertCircle />}
                        <div className="flex flex-1 flex-wrap items-center justify-between gap-3">
                          <AlertDescription>
                            {quizScore >= 2
                              ? `Congratulations! You passed the quiz with score ${quizScore}/${activeQuizzes.length}. Progress updated.`
                              : `You scored ${quizScore}/${activeQuizzes.length}. A minimum score of 2/3 is required to pass. Please retry the lesson.`}
                          </AlertDescription>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedAnswers({});
                              setQuizSubmitted(false);
                              setQuizScore(0);
                            }}
                          >
                            Try again
                          </Button>
                        </div>
                      </Alert>
                    ) : (
                      <Button
                        disabled={Object.keys(selectedAnswers).length < activeQuizzes.length}
                        onClick={handleQuizSubmit}
                      >
                        Submit answers
                      </Button>
                    )}
                  </div>
                </SectionCard>
              )}
            </div>
          ) : (
            <p className="text-sm text-body">Select a lesson from the syllabus to begin learning.</p>
          )}
        </div>

        {/* Right column - Syllabus Navigation panel */}
        <aside className="min-w-0 lg:col-span-4">
          <div className="overflow-hidden rounded-md border border-border bg-card">
            <div className="space-y-2 border-b border-border p-4">
              <h2 className="text-[15px] font-medium text-foreground">Learning progress</h2>
              <div className="flex items-center justify-between text-caption">
                <span className="font-medium text-body">{overallProgress}% Completed</span>
                <span className="text-muted-foreground">
                  {completedCount} of {lessons.length} lessons
                </span>
              </div>
              <Progress value={overallProgress} aria-label="Course progress" />

              {isCourseFinished && (
                <Alert variant="success" className="mt-3">
                  <Medal />
                  <div className="space-y-2">
                    <AlertTitle>Course Fully Completed!</AlertTitle>
                    <Button asChild size="sm" variant="outline">
                      <Link to="/student/certificates">View certificate</Link>
                    </Button>
                  </div>
                </Alert>
              )}
            </div>

            <nav aria-label="Lessons">
              <ol className="space-y-1 p-2">
                {lessons.map((lesson: Lesson, idx: number) => {
                  const isActive = idx === currentLessonIndex;
                  return (
                    <li key={lesson.id}>
                      <button
                        type="button"
                        aria-current={isActive ? "step" : undefined}
                        onClick={() => setCurrentLessonIndex(idx)}
                        className={cn(
                          "flex w-full items-start gap-2.5 rounded-sm border px-3 py-2.5 text-left transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                          isActive ? "border-border bg-accent" : "border-transparent",
                        )}
                      >
                        {lesson.completed ? (
                          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-label="Completed" />
                        ) : (
                          <Circle className="mt-0.5 size-4 shrink-0 text-hairline-strong" aria-label="Not completed" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span
                            className={cn(
                              "block text-[13px] leading-snug",
                              isActive || lesson.completed ? "font-medium text-foreground" : "text-body",
                            )}
                          >
                            {lesson.title}
                          </span>
                          <span className="mt-1 flex flex-wrap items-center gap-2">
                            <span className="text-caption text-muted-foreground">{lesson.duration}</span>
                            {lesson.video_watched_percent > 0 && (
                              <Badge variant="muted" className="text-[10px] leading-4">
                                {`Watched ${lesson.video_watched_percent}%`}
                              </Badge>
                            )}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>
        </aside>
      </div>

      {/* PDF Modal Reader */}
      <Dialog open={pdfOpen} onOpenChange={(o) => !o && setPdfOpen(false)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Learning PDF reader</DialogTitle>
          </DialogHeader>
          <div className="rounded-md border border-border bg-background px-4 py-8 text-center">
            <BookOpen className="mx-auto mb-4 size-10 text-muted-foreground" aria-hidden />
            <p className="mb-1 text-title text-foreground">Sample Course Notes & Learning Resource</p>
            <p className="mx-auto mb-5 max-w-md text-sm text-muted-foreground">
              This PDF document contains the lecture slides, code examples, and theoretical readings compiled specifically for this lesson module.
            </p>
            <Button asChild>
              <a href="/uploads/sample-learning-resource.pdf" download>
                <Download />
                Download PDF note file
              </a>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Fallback Mock data for demo mode
function getMockLessons(courseId: number): Lesson[] {
  return [
    {
      id: 101,
      course_id: courseId,
      title: "1. Overview of Word Processing Interface",
      description: "Learn how to navigate the Microsoft Word and Google Docs interfaces, highlighting ribbons, command tabs, document views, and options.",
      order: 1,
      video_url: "https://media.w3.org/2010/05/sintel/trailer_hd.mp4",
      pdf_url: "/uploads/sample-learning-resource.pdf",
      duration: "15 min",
      resources: JSON.stringify([
        { title: "Interface Navigation Cheat Sheet", url: "/uploads/sample-learning-resource.pdf", type: "pdf" }
      ]),
      quizzes: JSON.stringify([
        {
          question: "Where are the primary commands located in MS Word?",
          options: ["In the Status Bar", "On the Ribbon tabs", "Inside the scroll track", "None of the above"],
          correct_index: 1
        },
        {
          question: "Which view displays the document as it will appear in print?",
          options: ["Outline View", "Print Layout View", "Web Layout View", "Draft View"],
          correct_index: 1
        },
        {
          question: "Is Google Docs cloud-based?",
          options: ["Yes, Google Docs runs fully in the browser and cloud", "No, it is desktop-only"],
          correct_index: 0
        }
      ]),
      completed: true,
      video_watched_percent: 100,
      pdf_accessed: true,
      quiz_completed: true,
      quiz_score: 100
    },
    {
      id: 102,
      course_id: courseId,
      title: "2. Document Formatting and Text Styles",
      description: "Dive into paragraph spacing, custom text styles, fonts, applying themes, lists, and formatting standards to create highly readable layouts.",
      order: 2,
      video_url: "https://www.w3schools.com/html/mov_bbb.mp4",
      pdf_url: "/uploads/sample-learning-resource.pdf",
      duration: "20 min",
      resources: JSON.stringify([
        { title: "Formatting Styles Guidelines", url: "/uploads/sample-learning-resource.pdf", type: "pdf" }
      ]),
      quizzes: JSON.stringify([
        {
          question: "What is paragraph spacing?",
          options: ["Space before and after paragraphs", "Space between characters", "Space in margins"],
          correct_index: 0
        },
        {
          question: "Why should we use pre-defined text Styles?",
          options: ["To ensure consistent layout and easily generate table of contents", "Styles are not helpful", "It decreases file size"],
          correct_index: 0
        },
        {
          question: "Which shortcut aligns text center?",
          options: ["Ctrl + L", "Ctrl + E", "Ctrl + R", "Ctrl + J"],
          correct_index: 1
        }
      ]),
      completed: false,
      video_watched_percent: 45,
      pdf_accessed: false,
      quiz_completed: false,
      quiz_score: 0
    },
    {
      id: 103,
      course_id: courseId,
      title: "3. Layouts, Headers, and Section Breaks",
      description: "Understand page sizes, landscape vs portrait layouts, margins, inserting page numbers in headers/footers, and isolating formatting using section breaks.",
      order: 3,
      video_url: "https://media.w3.org/2010/05/sintel/trailer_hd.mp4",
      pdf_url: "/uploads/sample-learning-resource.pdf",
      duration: "18 min",
      resources: JSON.stringify([
        { title: "Layout Configuration Notes", url: "/uploads/sample-learning-resource.pdf", type: "pdf" }
      ]),
      quizzes: JSON.stringify([
        {
          question: "What does a Section Break do?",
          options: ["It isolates headers, footers, and margins from other pages", "It splits a word into two parts", "It restarts page numbering only"],
          correct_index: 0
        },
        {
          question: "How do you switch page orientation for a single page?",
          options: ["Insert section breaks before and after that page", "Use print margins setting", "It cannot be done in MS Word"],
          correct_index: 0
        },
        {
          question: "Where are page numbers usually inserted?",
          options: ["In Headers or Footers", "Directly in margins", "Inside paragraph blocks"],
          correct_index: 0
        }
      ]),
      completed: false,
      video_watched_percent: 0,
      pdf_accessed: false,
      quiz_completed: false,
      quiz_score: 0
    }
  ];
}
